import { ValueGetterFunc } from "@ag-grid-community/core";
import { Badge } from "@chakra-ui/layout";
import { invariant } from "@remix-run/router/history";
import { useGetAllocationsByLookupKey } from "features/drawdown/DrawdownUtils";
import {
  getHypotheticalWaterfallDataRetrieveQueryKey,
  hypotheticalWaterfallRetrieve,
  useHypotheticalWaterfallDataRetrieveSuspense,
  useHypotheticalWaterfallTableCreate,
} from "gen/endpoints/financial-reporting/financial-reporting";
import { useFundFamilyConfigRetrieveSuspense } from "gen/endpoints/fund-family-config/fund-family-config";
import { useEndOfPeriodReportDateDetailsRetrieveSuspense } from "gen/endpoints/next-end-of-period-report/next-end-of-period-report";
import { FundFamilyUseAllocationTableDataWithDefinition } from "gen/model/fundFamilyUseAllocationTableDataWithDefinition";
import { FundFamilyUseAllocationTableRow } from "gen/model/fundFamilyUseAllocationTableRow";
import { HypotheticalWaterfallData } from "gen/model/hypotheticalWaterfallData";
import { get, values } from "lodash-es";
import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { FundFamilyUseAllocationTable } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTable";
import { FundFamilyUseAllocationTableAmountCellRenderer } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableAmountCellRenderer";
import {
  FundFamilyUseAllocationTableAmountCellRendererSelector,
  FundFamilyUseAllocationTableAmountInfoCellRendererProps,
  FundFamilyUseAllocationTableGetAllocationFn,
} from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableProps";
import { useFundFamilyUseAllocationTableFormRowController } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableRowController";
import { useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse } from "shared/components/fund-family-use-allocation/FundFamilyUseAllocationTableUtils";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { MaybernTableBoxActions } from "shared/components/table/maybern/MaybernTableBoxActions";
import { useToast } from "shared/components/toast/useToast";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { sumMoneyStrings } from "shared/utils/MoneyUtils";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";

interface HypoWaterfallDateInformation {
  startDate: string;
  endDate: string;
  dateRangeRichValue: string;
}

export interface HypotheticalWaterfallInputProps {
  endOfPeriodReportId?: string;
  dateInformation?: HypoWaterfallDateInformation;
}

export const CustomHypoInputRender = React.forwardRef<
  HTMLInputElement,
  FundFamilyUseAllocationTableAmountInfoCellRendererProps
>((props, ref) => {
  const isPnlInput = props.data?.category_id === "p_and_l";
  return (
    <FundFamilyUseAllocationTableAmountCellRenderer
      {...props}
      forceReadOnly={!isPnlInput}
      ref={ref}
    />
  );
});

CustomHypoInputRender.displayName = "CustomHypoInputRender";

const amountCellRendererSelector: FundFamilyUseAllocationTableAmountCellRendererSelector =
  () => CustomHypoInputRender;

export const HypotheticalWaterfallInputV2: React.FC<
  HypotheticalWaterfallInputProps
> = ({ endOfPeriodReportId, dateInformation }) => {
  const { fundFamilyId } = useRequiredStringParams();

  const { t } = useTranslation(["financialReporting"]);
  const navigate = useNavigate();

  const { data: fundFamilyConfig } =
    useFundFamilyConfigRetrieveSuspense(fundFamilyId);
  const { data: reportData } =
    useEndOfPeriodReportDateDetailsRetrieveSuspense(fundFamilyId);

  const evaluatedDateInformation = dateInformation || {
    startDate: reportData.start_date,
    endDate: reportData.end_date,
    dateRangeRichValue: reportData.period_rich_value,
  };

  const baseRoute = rootRoute({})
    .fundFamilies({})
    .fundFamily({ fundFamilyId })
    .financialReporting({});

  const [hypotheticalWaterfallData, setHypotheticalWaterfallData] =
    useState<HypotheticalWaterfallData>();

  const {
    mutateAsync: createHypotheticalWaterfall,
    variables: createVariables,
    error: createError,
  } = useHypotheticalWaterfallTableCreate();

  const onCeleryTaskFinish = async () => {
    if (!hypotheticalWaterfallData) {
      return;
    }
    // need to refresh this data to get the is_carry_clawback_available flag
    const data = await hypotheticalWaterfallRetrieve(
      fundFamilyId,
      hypotheticalWaterfallData.end_of_period_report_id,
      hypotheticalWaterfallData.id,
    );
    if (data.is_carry_clawback_available) {
      navigate(
        baseRoute
          .hypotheticalWaterfall({
            hypotheticalWaterfallId: hypotheticalWaterfallData.id,
            endOfPeriodReportId:
              hypotheticalWaterfallData.end_of_period_report_id,
          })
          .designateProtectedCarry({}).$,
      );
      return;
    }
    navigate(
      baseRoute
        .hypotheticalWaterfall({
          hypotheticalWaterfallId: hypotheticalWaterfallData.id,
          endOfPeriodReportId:
            hypotheticalWaterfallData.end_of_period_report_id,
        })
        .review({})
        .waterfallDetails({}).$,
    );
  };

  const toast = useToast();

  const onSuccessToast = useCallback(() => {
    toast.showToast({
      message: t("financialReporting:hypoCreate.toast.success"),
      variant: "success",
      isClosable: true,
    });
  }, [toast, t]);
  const onErrorToast = useCallback(() => {
    toast.showToast({
      message: t("financialReporting:hypoCreate.toast.error"),
      variant: "error",
      isClosable: true,
    });
  }, [toast, t]);
  const renderToasts = useCallback(
    (isError: boolean) => {
      if (isError) {
        onErrorToast();
      } else {
        onSuccessToast();
      }
    },
    [onErrorToast, onSuccessToast],
  );

  useCeleryWorkflowPoll(
    hypotheticalWaterfallData
      ? [hypotheticalWaterfallData.celery_workflow_id]
      : undefined,
    renderToasts,
    onCeleryTaskFinish,
    onCeleryTaskFinish,
  );

  const valueGetter = useCallback<
    ValueGetterFunc<FundFamilyUseAllocationTableRow>
  >((params) => {
    const colId = params.column.getColId();
    // TODO ENG-8118
    if (params.node?.data?.column_data && colId === "total") {
      const total = values(params.node.data.funds).map(
        (fund) => fund.amount.amount,
      );
      return sumMoneyStrings(total);
    }
    return get(params.data, params.column.getColDef().field!);
  }, []);

  const { data: initialData } = useHypotheticalWaterfallDataRetrieveSuspense(
    fundFamilyId,
    {
      effective_date: evaluatedDateInformation.endDate,
      start_date: evaluatedDateInformation.startDate,
      end_of_period_report_id: endOfPeriodReportId,
    },
  );

  // TODO resolver is awkward cuz there are read only rows
  const form = useForm<FundFamilyUseAllocationTableDataWithDefinition>({
    defaultValues: {
      rows: initialData.rows,
    },
  });
  const getErrorForCell =
    useGetFundFamilyUseAllocationTableDataGetErrorForCellFnFromResponse(
      createError,
      useCallback(
        (rowId) =>
          createVariables?.data.data.rows.findIndex((it) => it.id === rowId),
        [createVariables],
      ),
      "data.rows",
    );

  const { rows, onRowChange } =
    useFundFamilyUseAllocationTableFormRowController(form, "rows");

  const tableData = useMemo(() => ({ rows }), [rows]);

  const getAllocationLookupKey = useGetAllocationsByLookupKey(
    fundFamilyId,
    useMemo(
      () =>
        tableData.rows
          .filter((row) => row.column_data?.type === "pnl_input")
          .map((row) => {
            invariant(row.column_data?.type === "pnl_input");
            return {
              transaction_code_id: row.column_data.transaction_code_id || null,
              investment_id: null,
            };
          }),
      [tableData.rows],
    ),
    evaluatedDateInformation.endDate,
  );

  const getAllocation =
    useCallback<FundFamilyUseAllocationTableGetAllocationFn>(
      (params) => {
        const columnData = params.row.column_data;
        if (
          // HUGE GOTCHA
          // TODO ENG-8118
          params.row.category_id === "p_and_l" &&
          columnData?.type == "pnl_input"
        ) {
          const fundAllocations = getAllocationLookupKey?.({
            transaction_code_id: columnData.transaction_code_id || null,
            investment_id: null,
          });
          const fundInfo = fundAllocations?.find(
            (it) => it.id === params.fundDef.fund_id,
          );
          if (!fundInfo) {
            return undefined;
          }
          return {
            ownershipPercentage: fundInfo.ownershipPercentage,
            allocationRuleId: fundInfo.allocationRuleId,
            allocationRuleName: fundInfo.allocationRuleName,
          };
        }
        return undefined;
      },
      [getAllocationLookupKey],
    );

  const isLoading =
    form.formState.isSubmitting ||
    !!hypotheticalWaterfallData?.celery_workflow_id;

  const invalidateQuery = useInvalidateQueryKeys();

  const onSubmit = async (
    data: FundFamilyUseAllocationTableDataWithDefinition,
  ) => {
    const hypotheticalWaterfallResult = await createHypotheticalWaterfall({
      fundFamilyId,
      data: {
        end_date: evaluatedDateInformation.endDate,
        start_date: evaluatedDateInformation.startDate,
        end_of_period_report_id: endOfPeriodReportId,
        data: {
          ...data,
          // TODO ENG-8118
          rows: rows.filter((row) => row.category_id === "p_and_l"),
        },
      },
    });
    await invalidateQuery(
      getHypotheticalWaterfallDataRetrieveQueryKey(fundFamilyId, {
        effective_date: evaluatedDateInformation.endDate,
        end_of_period_report_id: endOfPeriodReportId,
      }),
    );
    setHypotheticalWaterfallData(hypotheticalWaterfallResult);
  };

  return (
    <PageContent isFullHeight={true}>
      <PageHeader
        size="lg"
        title={t("financialReporting:hypoCreate.title")}
        helperText={t("financialReporting:hypoCreate.instructions")}
        badge={<Badge>{evaluatedDateInformation.dateRangeRichValue}</Badge>}
      />
      <PageBody>
        <Form form={form} onSubmit={onSubmit}>
          <TableBox>
            <TableBoxHeader>
              <TableBoxTitle>
                {t("financialReporting:hypoCreate.tableTitle", {
                  quarter: evaluatedDateInformation.dateRangeRichValue,
                })}
              </TableBoxTitle>
              <MaybernTableBoxActions />
            </TableBoxHeader>
            <TableBoxBody>
              <FundFamilyUseAllocationTable
                onRowChange={onRowChange}
                getAllocation={getAllocation}
                tableDef={initialData.definition}
                tableData={tableData}
                valueGetter={valueGetter}
                amountCellRendererSelector={amountCellRendererSelector}
                getErrorForCell={getErrorForCell}
                context={{
                  roundingMethodology:
                    fundFamilyConfig.config.rounding_methodology,
                }}
              />
            </TableBoxBody>
          </TableBox>
          <FormFooter>
            <FormFooterActions>
              <FormCancelButton
                isDisabled={isLoading}
                onClick={() =>
                  navigate(
                    rootRoute({})
                      .fundFamilies({})
                      .fundFamily({ fundFamilyId })
                      .financialReporting({}).$,
                  )
                }
              />
              <FormSubmitButton isLoading={isLoading} isDisabled={isLoading}>
                {t("financialReporting:hypoCreate.submit")}
              </FormSubmitButton>
            </FormFooterActions>
          </FormFooter>
        </Form>
      </PageBody>
    </PageContent>
  );
};
